// frontend/src/pages/ProcessMaps.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { 
    ArrowLeftIcon, 
    PlusIcon, 
    DocumentDuplicateIcon, 
    CheckCircleIcon,
    ExclamationTriangleIcon,
    SparklesIcon 
} from '@heroicons/react/24/outline';
import ProcessFlowVisualizer from '../components/process/ProcessFlowVisualizer';

const ProcessMaps = () => {
    const { businessId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [business, setBusiness] = useState(null);
    const [processMaps, setProcessMaps] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [activeMapId, setActiveMapId] = useState(null);

    useEffect(() => {
        loadData();
    }, [businessId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const busRes = await axios.get(`/business/${businessId}`);
            setBusiness(busRes.data.data);

            const assessment = busRes.data.data.assessment;
            if (assessment) {
                const mapsRes = await axios.get(`/process-maps/assessment/${assessment.id}`);
                setProcessMaps(mapsRes.data.data);
                if (mapsRes.data.data.length > 0) {
                    setActiveMapId(mapsRes.data.data[0].id);
                }
            }

            const tempRes = await axios.get('/process-maps/templates');
            setTemplates(tempRes.data.data);
        } catch (error) {
            toast.error('Failed to load process maps data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateFromTemplate = async (templateId) => {
        if (!business?.assessment?.id) {
            toast.error('Please complete a business assessment first');
            return;
        }

        try {
            const response = await axios.post('/process-maps/from-template', {
                templateId,
                assessmentId: business.assessment.id
            });
            toast.success('Process map created from template!');
            setShowTemplateModal(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create map from template');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading process maps...</div>
            </div>
        );
    }

    const activeMap = processMaps.find(m => m.id === activeMapId) || processMaps[0];

    return (
        <div className="w-full space-y-6">
            <Link to={`/business/${businessId}`} className="inline-flex items-center text-sm text-gray-600 hover:text-black space-x-1">
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to Business Details</span>
            </Link>

            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex justify-between items-center flex-wrap gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Business Process Maps</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Map out current operations, detect bottlenecks, and pinpoint AI automation potential for {business?.name}.
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setShowTemplateModal(true)}
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 flex items-center space-x-1"
                        >
                            <DocumentDuplicateIcon className="w-4 h-4" />
                            <span>Use Template</span>
                        </button>
                        <Link
                            to={`/business/${businessId}/process-map/builder`}
                            className="px-4 py-2 bg-black text-white rounded-md text-sm font-medium hover:bg-gray-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>New Process Map</span>
                        </Link>
                    </div>
                </div>

                {!business?.assessment && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md text-sm">
                        ⚠️ Assessment needed: Please complete an assessment before creating process maps.
                    </div>
                )}
            </div>

            {processMaps.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center space-y-3">
                    <p className="text-gray-500">No process maps created yet for this business.</p>
                    <div className="flex justify-center space-x-3">
                        <button
                            onClick={() => setShowTemplateModal(true)}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
                        >
                            Explore Templates
                        </button>
                        <Link
                            to={`/business/${businessId}/process-map/builder`}
                            className="px-4 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-800"
                        >
                            Build From Scratch
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* Maps list sidebar */}
                    <div className="space-y-2 md:col-span-1">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">Maps ({processMaps.length})</h3>
                        {processMaps.map((map) => (
                            <button
                                key={map.id}
                                onClick={() => setActiveMapId(map.id)}
                                className={`w-full text-left p-3 rounded-md border text-sm transition-all ${
                                    activeMapId === map.id 
                                        ? 'bg-black text-white border-black shadow' 
                                        : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <div className="font-medium truncate">{map.name}</div>
                                <div className="flex justify-between items-center mt-1 text-xs opacity-75 capitalize">
                                    <span>{map.status}</span>
                                    <span>{map.steps?.length || 0} steps</span>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Active map display */}
                    <div className="md:col-span-3 bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                        {activeMap && (
                            <>
                                <div className="flex justify-between items-start border-b border-gray-100 pb-4">
                                    <div>
                                        <div className="flex items-center space-x-2">
                                            <h2 className="text-xl font-bold text-gray-900">{activeMap.name}</h2>
                                            <span className="px-2 py-0.5 text-xs rounded bg-gray-100 font-medium capitalize">
                                                {activeMap.status}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">{activeMap.description}</p>
                                    </div>
                                    <Link
                                        to={`/business/${businessId}/process-map/builder?mapId=${activeMap.id}`}
                                        className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium hover:bg-gray-50"
                                    >
                                        Edit Map
                                    </Link>
                                </div>

                                {activeMap.mentor_feedback && (
                                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                                        <strong>Mentor Feedback:</strong> {activeMap.mentor_feedback}
                                    </div>
                                )}

                                <ProcessFlowVisualizer 
                                    steps={activeMap.steps || []}
                                    bottlenecks={activeMap.bottlenecks || []}
                                    aiOpportunities={activeMap.ai_opportunities || []}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Template Selection Modal */}
            {showTemplateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto space-y-4">
                        <div className="flex justify-between items-center border-b border-gray-100 pb-3">
                            <h3 className="text-lg font-bold text-gray-900">Select Process Map Template</h3>
                            <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-black text-xl">&times;</button>
                        </div>

                        <div className="space-y-3">
                            {templates.map((tpl) => (
                                <div key={tpl.id} className="p-4 border border-gray-200 rounded-lg hover:border-black transition-colors">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <span className="text-xs font-semibold px-2 py-0.5 bg-gray-100 rounded text-gray-700">{tpl.category}</span>
                                            <h4 className="font-semibold text-gray-900 mt-1">{tpl.name}</h4>
                                            <p className="text-xs text-gray-600 mt-1">{tpl.description}</p>
                                        </div>
                                        <button
                                            onClick={() => handleCreateFromTemplate(tpl.id)}
                                            className="px-3 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-800 font-medium"
                                        >
                                            Use Template
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProcessMaps;
